import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getReportData } from '../../../../../api/reports';
import { Loader, Table } from '../../../../../common/components';
import { useSetLoading } from '../../../../../common/hooks/appearance';
import {
  fetchDashboardFilterData,
  pnFuneralTrustAgingTableColumns,
} from '../../../../../utils/constant';
import * as handlers from '../../../../../utils/handlers';
import DownloadModal from '../../../downloadModal/downloadModal';
import EmailModal from '../../../emailModal/emailModal';
import ItemEmptyTile from '../../../item-empty-tile/item-empty-tile';
import Toolbar from '../../../toolbar/toolbar';
import PNFuneralTrustAgingSummaryRow from './pnFuneralTrustAgingSummaryRow';

const PNFuneralTrustAgingSummary = ({ tableData, date }) => {
  const filterData = useSelector((state) => state.filters.dashboardMultiSelectFilters);
  const [loading, setLoading] = useState(false);
  const [pnfTrustSummary, setPNFTrustSummary] = useState([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [initialRenderDone, setInitialRenderDone] = useState(false);

  const toggleEmailModal = () => {
    if (pnfTrustSummary?.length) {
      setIsEmailModalOpen(!isEmailModalOpen);
    }
  };

  const toggleDownloadModal = () => {
    if (pnfTrustSummary?.length) {
      setIsDownloadModalOpen(!isDownloadModalOpen);
    }
  };

  useEffect(() => {
    if (!initialRenderDone) {
      setInitialRenderDone(true);
      return;
    }
    getUpdatedTableData();
  }, [filterData]);

  useEffect(() => {
    setPNFTrustSummary(tableData);
  }, [tableData]);

  const getUpdatedTableData = async () => {
    setLoading(true);
    try {
      const requestParams = {
        ...fetchDashboardFilterData(filterData),
        reportDate: date,
        timezone: moment.tz.guess(),
      };
      const { data } = await getReportData('pnFuneralTrustAgingSummary', requestParams);
      setPNFTrustSummary(data.data?.items || []);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      handlers.apiErrorHandler(error);
    }
  };

  const LoaderComponent = () => {
    if (loading) {
      return <Loader />;
    }
    return <ItemEmptyTile />;
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      {pnfTrustSummary && pnfTrustSummary?.length > 0 ? (
        <>
          <Toolbar
            title="PN Funeral Trust Aging"
            subTitle={`As on Date: ${moment(date).format('MMM DD,YYYY')}`}
            optionOneLabel="Email Report"
            optionOneIcon="MdOutlineEmail"
            onOptionOneClick={toggleEmailModal}
            optionTwoLabel="Download Report"
            optionTwoIcon="FiDownload"
            onOptionTwoClick={toggleDownloadModal}
            isTableDataPresent={tableData?.length > 0}
            toolTipText="This report provides a summary of outstanding accounts receivable, categorizing them based on their aging periods."
          />
          <Table
            headers={pnFuneralTrustAgingTableColumns}
            bordered
            responsive
            className="dashboard-table"
          >
            <tbody>
              {pnfTrustSummary?.map((rowData, rowNumber) => (
                <PNFuneralTrustAgingSummaryRow key={rowNumber} rowData={rowData} id={rowNumber} />
              ))}
            </tbody>
          </Table>
        </>
      ) : (
        <div className="loader-empty">
          <LoaderComponent />
        </div>
      )}

      <EmailModal
        isOpen={isEmailModalOpen}
        toggle={toggleEmailModal}
        reportType="pnFuneralTrustAgingSummary"
        apiValues={{ reportDate: date }}
        newFilters
      />
      <DownloadModal
        isOpen={isDownloadModalOpen}
        toggle={toggleDownloadModal}
        reportType="pnFuneralTrustAgingSummary"
        urlParams={{ reportDate: date }}
        newFilters
      />
    </>
  );
};

PNFuneralTrustAgingSummary.propTypes = {
  tableData: PropTypes.shape([]).isRequired,
  date: PropTypes.string.isRequired,
};

export default PNFuneralTrustAgingSummary;
